import moment from 'moment';
import store from '@/store/store';
import { getCouleurCategorie, adressFullStringGetter } from '@/services/general/general';
import {
  TLPE,
  PRIX_NET,
  PRIX_BRUT,
  HONORAIRES,
  FRAIS_TECHNIQUE,
  FRAIS_TRAITEMENT,
  PROPERTIES_ECH,
  CATEGORIE_DEFAULT,
  CATEGORIE_LOCATION,
  CATEGORIE_COMMENTAIRE,
} from '@/configurations/configPanneau';

export function isEcheancierFictif(echeancier) {
  return echeancier.id === '00000000-0000-0000-0000-000000000000';
}
function isLigneLocation(ligne) {
  return ligne.produit?.supportCommercial?.categorieVente === CATEGORIE_LOCATION;
}
function isLigneCommentaire(ligne) {
  return ligne.type === CATEGORIE_COMMENTAIRE;
}
function isLigneTlpe(ligne) {
  return !!ligne.produit?.produitDivers?.tlpe;
}
export function isLigneHonoraires(ligne) {
  return !!ligne.produit?.produitDivers?.honoraire;
}
export function isLigneFraisTechnique(ligne, societe) {
  return ligne.produitId === societe.produitIdDefautFraisTechnique;
}
export function isLigneFraisTraitement(ligne, societe) {
  return ligne.produitId === societe.produitIdHonoraireFraisTraitement;
}
export function isLigneForfaitAudit(ligne, societe) {
  return ligne.produitId === societe.produitIdHonoraireForfaitaire;
}
function getFormatDateKey(dateDebut, dateFin) {
  return [moment(dateDebut).format('DDMMYYYY'), moment(dateFin).format('DDMMYYYY')].join('_');
}

export function getEcheanchier(propriety, ech, echeanciers, isFictif = false) {
  const tabPropriety = PROPERTIES_ECH.filter(p => p !== propriety);
  const dates = getFormatDateKey(ech.dateDebut, ech.dateFin);
  const echeancierLigne = {
    ...Object.fromEntries(tabPropriety.map(el => [el, 0])),
    dateDebut: ech.dateDebut,
    dateFin: ech.dateFin,
    [propriety]: ech.montant,
    isEcheancierFictif: isFictif || !!echeanciers[dates]?.isEcheancierFictif,
  };
  if (echeanciers[dates]) {
    tabPropriety.forEach((el) => {
      if (echeanciers[dates][el]) echeancierLigne[el] = echeanciers[dates][el];
    });
  }
  echeanciers[dates] = echeancierLigne;
}

function genererEcheancierLigneLocation(echeancier, echeanciers, getDataFunc) {
  const isFictif = isEcheancierFictif(echeancier);
  echeancier.isEcheancierFictif = isFictif;
  echeancier.echeancierLignes.forEach((ech) => {
    const { dateDebut, dateFin, prixBrut } = getDataFunc(ech);
    const dates = getFormatDateKey(dateDebut, dateFin);
    const echeancierLigne = {
      ...Object.fromEntries(PROPERTIES_ECH.map(el => [el, 0])),
      dateDebut,
      dateFin,
      prixBrut,
      prixNet: ech.montant,
      isEcheancierFictif: isFictif || !!echeanciers[dates]?.isEcheancierFictif,
    };
    PROPERTIES_ECH
      .filter(p => p !== PRIX_NET && p !== PRIX_BRUT)
      .forEach(p => {
        if (echeanciers[dates]?.[p]) echeancierLigne[p] = echeanciers[dates][p];
      });
    echeanciers[dates] = echeancierLigne;
  });
}

function genererEcheancierLigneAutre(echeanciers, echeancier, ligne, societe) {
  if (!echeancier) return;
  const isFictif = isEcheancierFictif(echeancier);
  echeancier.isEcheancierFictif = isFictif;
  const proprietes = [
    [TLPE, isLigneTlpe(ligne)],
    [HONORAIRES, isLigneHonoraires(ligne)],
    [FRAIS_TECHNIQUE, isLigneFraisTechnique(ligne, societe)],
    [FRAIS_TRAITEMENT, isLigneFraisTraitement(ligne, societe)],
  ];
  proprietes
    .filter(([, correspond]) => correspond)
    .forEach(([propriete]) => {
      echeancier.echeancierLignes.forEach((ech) => {
        getEcheanchier(propriete, ech, echeanciers, isFictif);
      });
    });
}

export function groupEcheancierByYear(echeanciers) {
  const echeanciersRegroupeParAn = {};
  Object.values(echeanciers).forEach(ech => {
    const annee = `${moment(ech.dateDebut).format('YYYY')}`;
    if (!echeanciersRegroupeParAn[annee]) {
      echeanciersRegroupeParAn[annee] = ech;
      return;
    }
    PROPERTIES_ECH.forEach(key => {
      echeanciersRegroupeParAn[annee][key] += ech[key];
    });
    echeanciersRegroupeParAn[annee].isEcheancierFictif = ech.isEcheancierFictif;
    if (moment(echeanciersRegroupeParAn[annee].dateDebut).isAfter(ech.dateDebut)) {
      echeanciersRegroupeParAn[annee].dateDebut = ech.dateDebut;
    }
    if (moment(echeanciersRegroupeParAn[annee].dateFin).isBefore(ech.dateFin)) {
      echeanciersRegroupeParAn[annee].dateFin = ech.dateFin;
    }
  });
  return echeanciersRegroupeParAn;
}

function ligneToProduit(ligne, echeanciers, societe, getDataFunc) {
  const data = getDataFunc();
  const { produitFace } = ligne.produit;
  return {
    codeFace: produitFace.codeFace || produitFace.codePanneau,
    code: produitFace.codeFace || produitFace.codePanneau,
    reference: ligne.produit.reference || '',
    codeAfficheur: ligne.produit.referenceConfrere || '',
    dateDebut: data.dateDebut,
    dateFin: data.dateFin,
    couleur: data.couleur,
    categorie: data.categorie,
    commercial: data.commercial,
    commentaire: data.commentaire,
    prixBrutProduit: data.prixBrutProduit,
    produitPatrimoine: data.produitPatrimoine,
    total: ligne.totalHt,
    id: ligne.produit.id,
    num: (data.index ?? 0) + 1,
    libelle: ligne.produit.libelle,
    urlProduit: ligne.cheminProduit,
    societeLibelle: societe.libelle,
    azimut: produitFace.azimut || 0,
    format: produitFace.format || '',
    ville: produitFace.adresse.ville || '',
    materiel: produitFace.materielType || '',
    adresse1: produitFace.adresse.adresse1 || '',
    codePostal: produitFace.adresse.codePostal || '',
    adresse: adressFullStringGetter(produitFace.adresse),
    echeanciers: Object.values(groupEcheancierByYear(echeanciers)),
    regie: ligne.produit.tiers ? ligne.produit.tiers.nomCommercial : '',
    coordonnees: {
      lat: produitFace.adresse.latitude || 0,
      lng: produitFace.adresse.longitude || 0,
    },
    tiersId: ligne.devisCommande?.intervenants?.map((el) => el.tiersId) || [],
  };
}

export default function formatProduits(ligne) {
  const societe = store.getters['dataTiers/societe'];
  let couleur = '';
  let categorie = '';
  let prixBrutProduit = 0;
  const echeanciers = {};
  ligne.lignesCommande.forEach((ligneCommande) => {
    const echeancier = ligneCommande.echeanciers.find(
      (el) => el.referenceEcheancier === ligneCommande.referenceEcheancier,
    );
    if (isLigneLocation(ligneCommande)) {
      const libelle = ligneCommande.libelle.replace('Location', '').trim();
      couleur = getCouleurCategorie(ligneCommande.produit);
      categorie = libelle || CATEGORIE_DEFAULT;
      if (echeancier) {
        const dateLigne = echeancier.echeancierLignes.map((ech) => {
          const newDateFin = echeancier.quantitePeriodicite === 0
            ? ligneCommande.dateFin
            : ech.dateFin;
          return {
            dateDebut: new Date(ech.dateDebut),
            dateFin: new Date(newDateFin),
          };
        });
        const minDateDebut = new Date(Math.min.apply(null, dateLigne.map((d) => d.dateDebut)));
        const maxDateFin = new Date(Math.max.apply(null, dateLigne.map((d) => d.dateFin)));
        prixBrutProduit = ligneCommande.prixUnitaire;
        genererEcheancierLigneLocation(echeancier, echeanciers, () => ({
          dateDebut: minDateDebut,
          dateFin: maxDateFin,
          prixBrut: prixBrutProduit,
        }));
      }
    }
    genererEcheancierLigneAutre(echeanciers, echeancier, ligneCommande, societe);
  });

  return ligneToProduit(ligne, echeanciers, societe, () => ({
    dateDebut: ligne.dateDebut,
    dateFin: ligne.dateFin,
    couleur,
    categorie,
    prixBrutProduit,
    produitPatrimoine: true,
    commercial: ligne.commercial,
    commentaire: ligne.lignesCommande.find(isLigneCommentaire)?.libelle ?? '',
  }));
}

export function limitProduits(produits, tiersId) {
  return produits.filter(produit => produit.tiersId.some(id => tiersId.includes(id)));
}

export function formatFace(ligne, index) {
  const societe = store.getters['dataTiers/societe'];
  let couleur = '';
  let categorie = '';
  let prixBrutProduit = 0;
  let dateDebut = null;
  let dateFin = null;
  const echeanciers = {};
  ligne.lignesCommande.forEach((ligneCommande) => {
    const echeancier = ligneCommande.echeanciers.find((el) => {
      return el.referenceEcheancier === ligneCommande.referenceEcheancier;
    });
    if (isLigneLocation(ligneCommande)) {
      const libelle = ligneCommande.libelle.replace('Location', '').trim();
      couleur = getCouleurCategorie(ligneCommande.produit);
      categorie = libelle || CATEGORIE_DEFAULT;
      dateDebut = ligneCommande.dateDebut;
      dateFin = ligneCommande.dateFin;
      prixBrutProduit = ligneCommande.prixUnitaire;
      if (echeancier) {
        genererEcheancierLigneLocation(echeancier, echeanciers, (ech) => ({
          dateDebut: ech.dateDebut,
          dateFin: echeancier.quantitePeriodicite === 0
            ? ligneCommande.dateFin
            : ech.dateFin,
          prixBrut: prixBrutProduit,
        }));
      }
    }
    genererEcheancierLigneAutre(echeanciers, echeancier, ligneCommande, societe);
  });

  return ligneToProduit(ligne, echeanciers, societe, () => ({
    index,
    dateDebut,
    dateFin,
    couleur,
    categorie,
    commercial: ligne.lignesCommande[0].commercial,
    commentaire: ligne.lignesCommande.find(isLigneCommentaire)?.libelle ?? '',
  }));
}
